/**
 * Expanded detail for a session.
 */
import * as React from 'react';
import type { Session, SessionTab } from '@/types/remote/session.types';
import type { ReportDetail } from '@/types/remote/report.types';
import { useSessionLiveKitPresence } from '@/hooks/useSessionsRemote';
import { useGetReportBySession } from '@/hooks/useReportsRemote';
import {
  CANCELLATION_REASON_CONFIG,
  SESSION_DETAIL_TAB_CONFIGS,
  type SessionDetailTab,
  SESSION_REMOTE_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import {
  canShowJoinButton,
  getEffectiveStatusConfig,
  getSessionFacilitators,
  parseSessionDate,
} from '@/components/sessions/remote/session-table-utils';
import { LabelBadge } from '@/components/common/LabelBadge';
import { VisioPopupBlockedModal } from '@/components/common/modals/VisioPopupBlockedModal';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { useJoinRemoteMeeting } from '@/hooks/useJoinRemoteMeeting';
import { useRoleConfig } from '@/hooks/useRoleConfig';
import { History as HistoryIcon } from 'lucide-react';
import {
  SessionRescheduleBanner,
  SessionRescheduleTimelineItems,
} from '@/components/sessions/SessionRescheduleDetails';
import { SessionDetailsTab } from './session-detail-tabs/SessionDetailsTab';
import { SessionParticipantsTab } from './session-detail-tabs/SessionParticipantsTab';
import { SessionReportsTab } from './session-detail-tabs/SessionReportsTab';

/** Props for {@link SessionDetails}. */
interface SessionDetailsProps {
  session: Session;
  activeTab?: SessionTab;
  pageActions?: {
    canJoinWelcomeMeeting?: boolean;
    canJoinInformationMeeting?: boolean;
  };
}

/** Active pane on the remote session details panel. */
type DetailTab = SessionDetailTab;

/** Expanded detail for a session. */
export const SessionDetails: React.FC<SessionDetailsProps> = ({
  session,
  activeTab,
  pageActions,
}) => {
  const [activeDetailTab, setActiveDetailTab] = React.useState<DetailTab>('details');
  const { isJoining, join, popupBlocked } = useJoinRemoteMeeting();
  const roleConfig = useRoleConfig();

  const isInProgress = session.status === 'IN_PROGRESS';
  const showJoinButton = canShowJoinButton(session, pageActions);

  /** Stop row-toggle, then open the LiveKit meeting. */
  const handleJoinMeeting = (e: React.MouseEvent) => {
    e.stopPropagation();
    void join(session.id);
  };

  const { data: presenceResponse, isLoading: presenceLoading } = useSessionLiveKitPresence(
    session.id,
    { refetchInterval: activeDetailTab === 'participants' ? 5000 : false },
  );

  const shouldFetchReport = activeDetailTab === 'reports';
  const { data: reportDetailRaw, isLoading: reportLoading } = useGetReportBySession(session.id, {
    enabled: shouldFetchReport,
  });
  const reportDetail = (reportDetailRaw as ReportDetail) ?? undefined;
  const reportNotFound = shouldFetchReport && !reportLoading && reportDetailRaw === null;

  const statusConfig = getEffectiveStatusConfig(session.effectiveStatus ?? session.status);
  const typeConfig = session.type ? SESSION_REMOTE_TYPE_CONFIG[session.type] : null;

  const sessionDate = parseSessionDate(String(session.sessionDate ?? ''));

  const isTutoringSession =
    session.type === 'individual_tutoring' || session.type === 'group_tutoring';
  const isFieldEventSession = session.type === 'workshop';
  const isMeetingSession =
    session.type === 'information_meeting' || session.type === 'welcome_meeting';

  const configAnimators =
    !isTutoringSession && !isFieldEventSession ? getSessionFacilitators(session) : [];
  const hasConfigContent =
    (!isMeetingSession && !!session.establishment) ||
    (isTutoringSession && !!session.managedBy) ||
    configAnimators.length > 0 ||
    !!(session.programName || session.programId) ||
    !!session.createdBy ||
    showJoinButton;

  const DETAIL_TABS: TabItem<DetailTab>[] = SESSION_DETAIL_TAB_CONFIGS.filter(
    ({ id }) => id !== 'participants' || !isFieldEventSession,
  ).map(({ id, label, icon: Icon }) => ({ id, label, icon: <Icon className="w-4 h-4" /> }));

  return (
    <div className="space-y-4">
      <VisioPopupBlockedModal {...popupBlocked} />

      {session.status === 'CANCELLED' &&
        session.cancellation &&
        (() => {
          const code = (
            session.cancellation.reasonCode ??
            session.cancellation.reason ??
            ''
          ).toUpperCase();
          const cfg = CANCELLATION_REASON_CONFIG[code];
          const detail = session.cancellation.reasonDetail ?? session.cancellation.comment;
          const isOther = code === 'OTHER';
          let reasonNode: React.ReactNode;
          if (isOther && detail) {
            reasonNode = (
              <span className="text-sm italic" style={{ color: 'var(--color-1-nuance-1)' }}>
                "{detail}"
              </span>
            );
          } else if (cfg) {
            reasonNode = (
              <LabelBadge
                label={cfg.label}
                icon={cfg.icon}
                variant={cfg.badgeVariant}
                color={cfg.badgeColor}
                size="sm"
              />
            );
          } else {
            reasonNode = (
              <span className="text-sm font-medium" style={{ color: 'var(--color-1-nuance-1)' }}>
                {code || '—'}
              </span>
            );
          }
          return (
            <div
              className="rounded-lg p-4 border space-y-2"
              style={{
                background: 'color-mix(in srgb, rgb(var(--status-error)) 8%, transparent)',
                borderColor: 'rgb(var(--status-error))',
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-sm font-semibold"
                  style={{ color: 'rgb(var(--status-error))' }}
                >
                  Séance annulée
                </span>
                {session.cancellation.cancelledAt && (
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    — {new Date(session.cancellation.cancelledAt).toLocaleString('fr-FR')}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  Motif :
                </span>
                {reasonNode}
              </div>
              {!isOther && detail && (
                <p className="text-xs italic" style={{ color: 'var(--muted-foreground)' }}>
                  "{detail}"
                </p>
              )}
            </div>
          );
        })()}

      <SessionRescheduleBanner reschedules={session.reschedules} />

      <Tabs
        items={DETAIL_TABS}
        activeTab={activeDetailTab}
        onTabChange={(id) => setActiveDetailTab(id as DetailTab)}
        size="md"
        ariaLabel="Onglets séance"
      />

      <div className="p-4">
        {activeDetailTab === 'details' && (
          <SessionDetailsTab
            session={session}
            activeTab={activeTab}
            typeConfig={typeConfig}
            statusConfig={statusConfig}
            sessionDate={sessionDate}
            isTutoringSession={isTutoringSession}
            isFieldEventSession={isFieldEventSession}
            isMeetingSession={isMeetingSession}
            configAnimators={configAnimators}
            hasConfigContent={hasConfigContent}
            showJoinButton={showJoinButton}
            isJoining={isJoining}
            isInProgress={isInProgress}
            handleJoinMeeting={handleJoinMeeting}
            roleConfig={roleConfig}
          />
        )}

        {activeDetailTab === 'participants' && (
          <SessionParticipantsTab
            session={session}
            activeTab={activeTab}
            presenceResponse={presenceResponse}
            presenceLoading={presenceLoading}
          />
        )}

        {activeDetailTab === 'reports' && (
          <SessionReportsTab
            session={session}
            reportDetail={reportDetail}
            reportLoading={reportLoading}
            reportNotFound={reportNotFound}
          />
        )}

        {activeDetailTab === 'history' && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <HistoryIcon className="w-4 h-4" />
              Timeline des événements
            </h4>
            <div className="relative border-l-2 border-gray-200 pl-4 space-y-4">
              {session.createdAt && (
                <div className="relative">
                  <div className="absolute -left-6 w-4 h-4 rounded-full bg-blue-600" />
                  <div className="text-xs text-gray-500">
                    {new Date(session.createdAt).toLocaleString()}
                  </div>
                  <div className="text-sm font-medium">Séance créée</div>
                  {session.createdBy && (
                    <div className="text-xs text-gray-600">
                      Par {session.createdBy.firstName} {session.createdBy.lastName}
                    </div>
                  )}
                </div>
              )}
              {session.updatedAt && session.updatedAt !== session.createdAt && (
                <div className="relative">
                  <div className="absolute -left-6 w-4 h-4 rounded-full bg-yellow-600" />
                  <div className="text-xs text-gray-500">
                    {new Date(session.updatedAt).toLocaleString()}
                  </div>
                  <div className="text-sm font-medium">Séance modifiée</div>
                </div>
              )}
              <SessionRescheduleTimelineItems reschedules={session.reschedules} />
              {session.status === 'CANCELLED' &&
                session.cancellation &&
                (() => {
                  const code = (
                    session.cancellation.reasonCode ??
                    session.cancellation.reason ??
                    ''
                  ).toUpperCase();
                  const cfg = CANCELLATION_REASON_CONFIG[code];
                  const detail = session.cancellation.reasonDetail ?? session.cancellation.comment;
                  const isOther = code === 'OTHER';
                  let reasonNode: React.ReactNode;
                  if (isOther && detail) {
                    reasonNode = <span className="text-xs italic text-gray-700">"{detail}"</span>;
                  } else if (cfg) {
                    reasonNode = (
                      <LabelBadge
                        label={cfg.label}
                        icon={cfg.icon}
                        variant={cfg.badgeVariant}
                        color={cfg.badgeColor}
                        size="sm"
                      />
                    );
                  } else {
                    reasonNode = <span className="text-xs font-medium">{code || '—'}</span>;
                  }
                  return (
                    <div className="relative">
                      <div className="absolute -left-6 w-4 h-4 rounded-full bg-red-600" />
                      {session.cancellation.cancelledAt && (
                        <div className="text-xs text-gray-500">
                          {new Date(session.cancellation.cancelledAt).toLocaleString('fr-FR')}
                        </div>
                      )}
                      <div className="text-sm font-medium">Séance annulée</div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <span className="text-xs text-gray-500">Motif :</span>
                        {reasonNode}
                      </div>
                      {!isOther && detail && (
                        <p className="text-xs italic text-gray-500 mt-1">"{detail}"</p>
                      )}
                    </div>
                  );
                })()}
              {session.status === 'COMPLETED' && (
                <div className="relative">
                  <div className="absolute -left-6 w-4 h-4 rounded-full bg-green-600" />
                  <div className="text-xs text-gray-500">{sessionDate.toLocaleString()}</div>
                  <div className="text-sm font-medium">Séance réalisée</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * @deprecated Use {@link SessionDetails} instead.
 */
export const renderSessionDetails = (session: Session, activeTab?: SessionTab): React.ReactNode => (
  <SessionDetails session={session} activeTab={activeTab} />
);
